import type { BillingSummaryDto, OrderDto } from "@/shared/api/types";
import { renewalOf } from "@/shared/utils/subscription";

/** The renewal invoice being replaced, and what the renewal costs now. */
export interface StaleRenewal {
  /** `renewal.orderId`: the invoice the worker is replacing. */
  orderId: string;
  /** What the renewal costs today (`renewal.totalPaise`); the replacement will say this. */
  totalPaise: number | null;
}

/**
 * The renewal invoice that says something other than what the renewal costs today (ADR-0015 addendum of
 * 2026-10-04). Kumar's second finding on the Subscription page: a discount rule was added after the
 * renewal invoice had been raised, so the page said "Renews … for ₹5,408.96" beside "Pay ₹6,010.04".
 * The worker's `billing-reprice` job replaces such an invoice within seconds and `GET /billing` says when
 * one is waiting to be replaced (`renewal.orderCurrent === false`); this names which invoice and what
 * the renewal costs now. `null` = nothing is being replaced: no issued renewal invoice, or one that is
 * current.
 */
export function staleRenewalOf(billing: BillingSummaryDto | undefined): StaleRenewal | null {
  const renewal = renewalOf(billing);
  if (!renewal || renewal.orderCurrent !== false || renewal.orderStatus !== "issued" || !renewal.orderId) {
    return null;
  }
  return { orderId: renewal.orderId, totalPaise: renewal.totalPaise };
}

/**
 * The invoice that replaced the one an open `OrderDialog` is showing, or `null` when it was not replaced.
 *
 * A customer who opened the renewal invoice just before the worker re-priced it would otherwise be left
 * looking at the OLD order once the polling window ends — and, the replacement being current, its Pay link
 * would come back, onto an invoice the gateway has already cancelled. So once the order the dialog holds
 * is no longer open, and the renewal now names a DIFFERENT issued invoice that is, the dialog moves to it.
 * A renewal that was simply paid is not "replaced": its id stays the renewal's, now `paid`.
 */
export function replacementOf(
  shown: Pick<OrderDto, "id" | "kind"> | null,
  billing: BillingSummaryDto | undefined,
): OrderDto | null {
  if (!shown || shown.kind !== "renewal" || !billing) return null;
  if (billing.openOrders.some((o) => o.id === shown.id)) return null;
  const renewal = renewalOf(billing);
  if (!renewal || renewal.orderStatus !== "issued" || !renewal.orderId || renewal.orderId === shown.id)
    return null;
  return billing.openOrders.find((o) => o.id === renewal.orderId) ?? null;
}
